import React from "react";
import { render } from "react-dom";
import { Provider } from 'react-redux';
import { createStore, applyMiddleware, compose } from "redux";
import thunk from 'redux-thunk';
import ReduxQuerySync from 'redux-query-sync'; // https://github.com/Treora/redux-query-sync
import rootReducer from './store/reducers';
import App from './components/App';
import { initialize, selectFilter } from "./store/actions";
import { composeWithDevTools } from "@redux-devtools/extension";
import { defaultAttributeFilters } from "./store/reducers/parameters";

const ProductFinder = (() => {

  // TODO_REFRACTOR: use forwardRef or context instead of global variable
  // dom element references used across application.
  let countRef = React.createRef(),
    alternativeRef = React.createRef();

  let options;
  // default options
  // TODO should be stored in state
  let defaults = {
    el: 'product-finder-container', // dom elements id the app renders into
    path: '/', // TODO check if used
    settings: {
      basePath: "https://eu-p-industry-productfinder.wd.pisceu.panasonic.eu/api/productfinder",
      autoCompletePath: "https://industry.panasonic.eu/api/productfinder_suggest",
      alternativeSearchUrl: 'https://industry.panasonic.eu/api/productsearch/',
      generalSearchPath: 'https://industry.panasonic.eu/search',
      attributeSearchAutoCompletePath: "/api/productfinder_attributesearch_suggest", // deprecated
      stockSearchBasePath: '/stock-search',
      contactUsUrl: '/contact-us'
    },
    strings: {
      'STRING:TITLE': "Productfinder",
      'STRING:SEARCH_IN_STOCK': "Search Stock",
      'STRING:TITLE_SEARCH_MODE': "Product Search",
      'STRING:GROUP_LABEL': "Select group",
      'STRING:CATEGORY_LABEL': "Select category",
      'STRING:LINE_UP_LABEL': "Select line up",
      'STRING:SERIES_LABEL': "Select series",
      'STRING:FIND_YOUR_PRODUCT': "Find your product",
      'STRING:RESET_FILTERS': "Reset filters",
      'STRING:SWITCH_TO_SEARCH': "Switch to Search",
      'STRING:SWITCH_TO_PRODUCTFINDER': "Switch to Product Finder",
      'STRING:RESET_SEARCH': "Reset Search",
      'STRING:ENTER_SEARCH_TERM': "Enter Search Term",
      'STRING:SEARCH_TERM': "Search term",
      'STRING:SEE_RESULTS': "Search !count results",
      'STRING:MATCHES': "!count Matches",
      'STRING:FILTERS': "Filters",
      'STRING:CLOSE': "Close",
      'STRING:REFINE_RESULTS': "Refine results",
      'STRING:SELECT': "Select",
      'STRING:SEARCH_IN_RESULTS': "Search in results",
      'STRING:MANAGE_ATTRIBUTES': "Manage attributes",
      'STRING:ALL_ATTRIBUTES': "All attributes",
      'STRING:RESET_ALL': "Reset all",
      'STRING:PART_NO': "Part no.",
      'STRING:SEARCH_IN_STOCK': "Search Stock",
      'STRING:OF': "of",
      'STRING:PREV': "Previous",
      'STRING:NEXT': "Next",
      'STRING:NO_RESULTS_FOR': 'No results found matching your search for',
      'STRING:NO_RESULTS_FOUND': 'No results found matching your search!',
      'STRING:NO_RESULT_LIST_1': 'Make sure all words are spelled correctly',
      'STRING:NO_RESULT_LIST_2': 'Try different keywords',
      'STRING:NO_RESULT_LIST_3': 'Try more general keywords',
      'STRING:NO_RESULT_LIST_4': 'Try fewer keywords',
      'STRING:SEARCH_AGAIN': 'Search again',
      'STRING:CONTACT_US': 'Contact us',
      'STRING:SEARCH_COUNT_PRODUCT_RESULTS': '!count product results',
      'STRING:SEARCH_COUNT_ALTERNATIVE_RESULTS': '!count general results',
      'STRING:ERROR_FINDER': 'Please select at least one of the parameters above to search through our product range.'
    }
  };


/**
 * 
 * @param {*} opts options to override defaults
 */
const run = (opts) => {

  if (opts) {
    options = Object.assign(defaults, opts);
  }

  const {settings, strings} = options;

  // get container dom element to render the application into
  const finderContainer = document.getElementById(defaults.el);
  if (finderContainer) {

    /**
     * using enhancer to connect path params with specific parts of the redux state
     * 
     * NOTE: this is one of the reasons why history back is not really working!!!
     */
    const storeEnhancer = ReduxQuerySync.enhancer({
      params: {
        group: {
          selector: state => state.parameters.filters.group,
          action: (value) => selectFilter('group', value)
        },
        category: {
          selector: state => state.parameters.filters.category,
          action: (value) => selectFilter('category', value)
        },
        lineup: {
          selector: state => state.parameters.filters.lineup,
          action: (value) => selectFilter('lineup', value)
        },
        series: {
          selector: state => state.parameters.filters.series,
          action: (value) => selectFilter('series', value),
          stringToValue: string => (string.length > 0 ? string.split(/,(?!\s)/g) : null),
          valueToString: value => (Array.isArray(value)) ? value.join(',') : value || '',
        },
        search: {
          selector: state => state.parameters.filters.search,
          action: (value) => selectFilter('search', value)
        },
        attribute_search: {
          selector: state => state.parameters.filters.attribute_search,
          action:  (value) => selectFilter('attribute_search', value)
        },
        sort: {
          selector: state => state.parameters.sort,
          action: value => {
            return { type: 'SELECT_SORT', sort_object: value };
          },
          stringToValue: string => {
            let values = string.split(';');
            values = values.map(item => {
              item = item.split(',');
              return {
                id: item[0],
                desc: (item[1] === 'desc') ? true : false,
              };
            });
            return values;
          },
          valueToString: value => {
            if (Array.isArray(value)) {
              value = value.map(item => {
                return item.id + ',' + (item.desc ? 'desc' : 'asc');
              });
              return value.join(';');
            }
          },
        },
        attributeFilters: {
          defaultValue: defaultAttributeFilters,
          selector: state => {
            if (Object.keys(state.parameters.attributeFilters).length === 0) {
              return defaultAttributeFilters;
            }
            return state.parameters.attributeFilters;
          },
          action: value => {
            return { type: 'SELECT_ATTRIBUTE_FILTERS', attributeFilters: value };
          },
          stringToValue: string => {
            if (!string || string === '[]') {
              return {};
            }
            let values = string.split(';');
            let ret = {};
            values.forEach(item => {
              item = item.split(':');
              item[1] = item[1].split(',');
              ret[item[0]] = item[1];
            });
            return ret;
          },
          valueToString: value => {
            if (value === '[]') {
              return '[]';
            }
            if (Object.keys(value).length === 0) {
              return '[]';
            }
            let values = [];
            for (let [key, item_value] of Object.entries(value)) {
              if (item_value) {
                values.push(key + ':' + item_value);
              }
            }
            if (values.length === 0) {
              return '[]';
            }
            return values.join(';');
          },
        },
        rangeAttributeFilters: {
          defaultValue: {},
          selector: state => {
            if (Object.keys(state.parameters.rangeAttributeFilters).length === 0) {
              return {};
            }
            return state.parameters.rangeAttributeFilters;
          },
          action: value => {
            return { type: 'SELECT_RANGE_ATTRIBUTE_FILTERS', rangeAttributeFilters: value };
          },
          stringToValue: string => {
            if (!string || string === '[]') {
              return {};
            }
            let values = string.split(';');
            let ret = {};
            values.forEach(item => {
              item = item.split(':');
              item[1] = item[1].split(',');
              ret[item[0]] = item[1];
            });
            return ret;
          },
          valueToString: value => {
            if (value === '[]') {
              return '[]';
            }
            if (Object.keys(value).length === 0) {
              return '[]';
            }
            let values = [];
            for (let [key, item_value] of Object.entries(value)) {
              if (item_value) {
                values.push(key + ':' + item_value);
              }
            }
            if (values.length === 0) {
              return '[]';
            }
            return values.join(';');
          },
        },
        page: {
          selector: state => state.parameters.page,
          action: value => ({ type: 'UPDATE_PAGE', page: value }),
          stringToValue: string => Number.parseInt(string) || 1,
          valueToString: value => `${value}`,
          defaultValue: 0,
        },
        removedAttributes: {
          defaultValue: '[]',
          selector: state => {
            if (Object.keys(state.parameters.removedAttributeFilters).length === 0) {
              return '[]';
            }
            return state.parameters.removedAttributeFilters;
          },
          action: value => {
            return { type: 'SET_REMOVED_ATTRIBUTE_COLUMN', removedAttributeFilters: value };
          },
          stringToValue: string => {
            if (!string || string === '[]') {
              return {};
            }
            let values = string.split(',');
            let ret = {};
            values.forEach(item => {
              ret[item] = item;
            });
            return ret;
          },
          valueToString: value => {
            if (Object.keys(value).length === 0) {
              return '[]';
            }
            let values = [];
            for (let [item_value] of Object.entries(value)) {
              if (item_value) {
                values.push(item_value);
              }
            }
            if (values.length === 0) {
              return '[]';
            }
            return values.join(',');
          }
        },
        mode: {
          selector: state => state.parameters.mode,
          action: value => {
            // Make sure we have the values.
            // Then return the action.
            return { type: 'SET_FILTER_MODE', mode: value };
          },
          defaultValue: 'DEFAULT'
        },
      },
      initialTruth: 'location',
      replaceState: false,
    });

    const reduxToolkitOptions = { trace: true };
    const composeEnhancers = composeWithDevTools(reduxToolkitOptions);


    // create store
    // setup thunk middleware to inject custom argument path eg. (dispatch, getState, path)
    const store = createStore(
      rootReducer,
      composeEnhancers(applyMiddleware(thunk.withExtraArgument(settings.basePath)), storeEnhancer)
    );
    // Introduce initialize action.
    store.dispatch(initialize());

    render(
      <Provider store={store}>
        <App settings={settings} strings={strings} />
      </Provider>,
      finderContainer
    );
  } else {
    console.debug('dom element for productfinder not found');
  }
}

return {
  run,
  countRef, alternativeRef,
  options: options || defaults
}

})();


window.ProductFinder = ProductFinder;




